import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Users, Briefcase, Route, GraduationCap, Upload, CalendarDays, UserPlus,
  Settings, TrendingUp, Copy, CheckCircle2, Plus, Eye, EyeOff, ChevronRight,
  Loader2, Clock, DollarSign, X, Shield, FileText, Receipt, BarChart3,
  ArrowUpRight, ArrowDownRight, Target, Percent, Bot, Sparkles, UserSearch, PhoneCall,
  Wallet, AlertTriangle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";

import { Skeleton } from "../components/ui/skeleton";
import { KpiSkeleton, CardSkeleton } from "../components/Skeletons";
import HotLeadsWidget from "../components/HotLeadsWidget";
import MetaInboxWidget from "../components/dashboard/MetaInboxWidget";
import { PartnerDashboard } from "../components/partners/PartnerDashboard";
import { useSociete } from "../contexts/SocieteContext";
import HealthCheckMiniCard from "../components/dashboard/HealthCheckMiniCard";
import OnboardingTour from "../components/OnboardingTour";
import OnboardingWizardPage from "./OnboardingWizardPage";

export default function DashboardPage() {
  const { user, axiosAuth, isAdmin } = useAuth();
  const { currentSociete } = useSociete();
  const { t } = useTranslation();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [widgets, setWidgets] = useState([]);
  const [showSettings, setShowSettings] = useState(false);

  const [showCreateClient, setShowCreateClient] = useState(false);
  const [clientForm, setClientForm] = useState({ name: "", email: "", password: "", company_name: "", phone: "" });
  const [creating, setCreating] = useState(false);

  const [showInvite, setShowInvite] = useState(false);
  const [invForm, setInvForm] = useState({ email: "", name: "", company_name: "" });
  const [invLink, setInvLink] = useState("");
  const [copied, setCopied] = useState(false);

  const [filter, setFilter] = useState("active");
  const [routineStats, setRoutineStats] = useState(null);
  const [tenantDash, setTenantDash] = useState(null);
  const [caOverview, setCaOverview] = useState(null);
  const [tresorerie, setTresorerie] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const isTenant = user?.role === "tenant_admin" || user?.role === "tenant_member";

  useEffect(() => {
    // Check onboarding status for tenant admins
    if (user?.role === "tenant_admin") {
      const checkApi = axiosAuth();
      checkApi.get("/onboarding/status").then(r => {
        if (!r.data?.completed) setShowOnboarding(true);
      }).catch(() => {});
    }
  }, [user?.role, axiosAuth]);

  useEffect(() => {
    const load = async () => {
      try {
        if (isTenant) {
          const r = await api.get("/tenant/dashboard");
          setTenantDash(r.data);
        } else if (isAdmin) {
          const [d, w, rs, ca, tr] = await Promise.all([
            api.get("/admin/dashboard"),
            api.get("/admin/widgets"),
            api.get("/ai-agents/crm/routines/stats").catch(() => ({ data: null })),
            api.get("/admin/dashboard/ca-overview").catch(() => ({ data: null })),
            api.get("/admin/dashboard/tresorerie").catch(() => ({ data: null })),
          ]);
          setDashboard(d.data);
          setWidgets(w.data.widgets || []);
          setRoutineStats(rs.data);
          setCaOverview(ca.data);
          setTresorerie(tr.data);
        } else {
          const [acc, srv, fec, mtg, home] = await Promise.all([
            api.get("/accompaniments").catch(() => ({ data: [] })),
            api.get("/services").catch(() => ({ data: [] })),
            api.get("/fec/files").catch(() => ({ data: [] })),
            api.get("/meetings").catch(() => ({ data: [] })),
            api.get("/tenant/client-home").catch(() => ({ data: {} })),
          ]);
          setDashboard({ accompaniments: acc.data, services: srv.data, fec: fec.data, meetings: mtg.data, clientHome: home.data || {} });
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const isVisible = (id) => {
    const w = widgets.find(w => w.id === id);
    return w ? w.visible !== false : true;
  };

  const toggleWidget = async (id) => {
    const updated = widgets.map(w => w.id === id ? { ...w, visible: !w.visible } : w);
    setWidgets(updated);
    await api.put("/admin/widgets", { widgets: updated });
  };

  const handleCreateClient = async () => {
    setCreating(true);
    try {
      await api.post("/admin/create-client", clientForm);
      setShowCreateClient(false);
      setClientForm({ name: "", email: "", password: "", company_name: "", phone: "" });
      const d = await api.get("/admin/dashboard");
      setDashboard(d.data);
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  const handleInvite = async () => {
    try {
      const r = await api.post("/admin/invite", invForm);
      const baseUrl = window.location.origin;
      setInvLink(`${baseUrl}/login?invite=${r.data.invite_token}`);
    } catch (e) { console.error(e); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(invLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="dashboard-loading">
      <Skeleton className="h-9 w-72" />
      <KpiSkeleton count={4} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CardSkeleton />
        <CardSkeleton />
      </div>
      <CardSkeleton />
    </div>
  );

  // ─── TENANT DASHBOARD ───
  if (isTenant && tenantDash) {
    const m = tenantDash.metrics || {};
    const STAGE_COLORS = { prospect: "#64748b", contact: "#3b82f6", qualification: "#8b5cf6", devis: "#f59e0b", negociation: "#f97316", gagne: "#22c55e", perdu: "#ef4444" };
    const stagesData = Object.entries(tenantDash.crm_stages || {}).map(([key, s]) => ({ name: s.label, value: s.count, fill: s.color || STAGE_COLORS[key] || "#64748b" }));
    const statusData = Object.entries(tenantDash.prospect_statuses || {}).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v }));
    const STATUS_COLORS = ["#3b82f6", "#8b5cf6", "#f7c935", "#f59e0b", "#22c55e", "#ef4444"];

    return (
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="tenant-dashboard">
        {showOnboarding && <OnboardingWizardPage onComplete={() => setShowOnboarding(false)} />}
        <OnboardingTour
          storageKey="blitzz_dashboard_tour_v1"
          autoStart={!showOnboarding && !user?.is_demo}
          steps={[
            { selector: "tenant-dashboard", title: "Bienvenue sur votre Dashboard 👋", description: "Voici votre poste de pilotage commercial. Découvrez en 4 étapes les nouveautés clés.", position: "bottom" },
            { selector: "tenant-kpi-prospects", title: "Vos KPIs en un coup d'œil", description: "Prospects, opportunités, taux de conversion et pipeline cumulé — tout est centralisé ici.", position: "bottom" },
            { selector: "hot-leads-widget", title: "À rappeler aujourd'hui 🔥", description: "L'IA analyse les appels Aircall et le pipeline pour ranker vos top leads chauds. Cliquez sur le téléphone vert pour appeler en un clic via Aircall.", position: "top" },
            { selector: "tenant-recent-prospects", title: "Activité récente", description: "Vos derniers prospects et opportunités. Cliquez sur n'importe quelle ligne pour ouvrir la fiche complète avec timeline + scoring IA + résumés Aircall.", position: "top" },
          ]}
        />
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-white">{tenantDash.tenant_name}</h1>
            {tenantDash.welcome_text ? (
              <p className="text-sm text-slate-400 mt-1">{tenantDash.welcome_text}</p>
            ) : (
              <p className="text-sm text-slate-500 mt-1">{t("dashboard.subtitle_default")}</p>
            )}
          </div>
          <span className="text-[10px] px-3 py-1 rounded-full font-bold bg-[#f7c935]/10 text-[#f7c935] uppercase tracking-wide">{tenantDash.plan}</span>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: t("dashboard.kpi_prospects"), value: m.total_prospects, icon: UserSearch, color: "#3b82f6" },
            { label: t("dashboard.kpi_opportunities"), value: m.total_opportunities, icon: TrendingUp, color: "#8b5cf6" },
            { label: t("dashboard.kpi_won"), value: m.won_opportunities, icon: CheckCircle2, color: "#22c55e", sub: `${m.conversion_rate}% conv.` },
            { label: t("dashboard.kpi_pipeline"), value: `${(m.pipeline_value || 0).toLocaleString("fr-FR")} EUR`, icon: DollarSign, color: "#f7c935" },
          ].map(k => (
            <div key={k.label} className="bg-[#14101c] border border-white/5 rounded-xl p-4 animate-fade-in-up" data-testid={`tenant-kpi-${k.label.toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-1">
                <k.icon className="w-4 h-4" style={{ color: k.color }} />
                <span className="text-[10px] text-slate-500 uppercase tracking-wide">{k.label}</span>
              </div>
              <div className="text-2xl font-black" style={{ color: k.color }}>{k.value}</div>
              {k.sub && <div className="text-[10px] text-slate-500 mt-0.5">{k.sub}</div>}
            </div>
          ))}
        </div>

        {/* Health Check synthétique (admin only) */}
        <div className="mb-6">
          <HealthCheckMiniCard />
        </div>

        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          {/* Pipeline Funnel */}
          {stagesData.length > 0 && (
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="tenant-pipeline-chart">
              <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#f7c935]" /> {t("dashboard.widget_crm_pipeline")}</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stagesData} layout="vertical">
                  <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} width={100} />
                  <Tooltip contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {stagesData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Prospect Status Distribution */}
          {statusData.length > 0 && (
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="tenant-prospect-chart">
              <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><UserSearch className="w-4 h-4 text-[#3b82f6]" /> {t("dashboard.widget_prospect_status")}</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {statusData.map((_, i) => <Cell key={i} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: t("dashboard.kpi_won_revenue"), value: `${(m.won_value || 0).toLocaleString("fr-FR")} EUR`, color: "#22c55e" },
            { label: t("dashboard.kpi_lost"), value: m.lost_opportunities, color: "#ef4444" },
            { label: t("dashboard.kpi_social_posts"), value: m.total_posts, color: "#3b82f6" },
            { label: t("dashboard.kpi_mail_campaigns"), value: m.total_campaigns, color: "#f59e0b" },
          ].map(k => (
            <div key={k.label} className="bg-[#0b0811] border border-white/5 rounded-lg p-3">
              <div className="text-[10px] text-slate-500 mb-0.5">{k.label}</div>
              <div className="text-lg font-bold" style={{ color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* Hot Leads à rappeler — IA ranking */}
        <div className="mb-4 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <HotLeadsWidget limit={5} minScore={50} />
          </div>
          <div>
            <MetaInboxWidget />
          </div>
        </div>

        {/* Iter 322 — Activité partenaires (société active) */}
        <div className="mb-4">
          <PartnerDashboard societeId={currentSociete?.societe_id || null} compact />
        </div>

        {/* Recent Activity */}
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="tenant-recent-prospects">
            <h3 className="text-sm font-bold text-white mb-3">{t("dashboard.widget_recent_prospects")}</h3>
            {(tenantDash.recent_prospects || []).length === 0 ? (
              <p className="text-sm text-slate-500 py-4">{t("dashboard.no_prospect_yet")}</p>
            ) : (tenantDash.recent_prospects || []).map(p => (
              <div key={p.prospect_id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center text-xs font-bold text-blue-400">{(p.first_name || "?")[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-white truncate">{p.first_name} {p.last_name}</div>
                  <div className="text-[10px] text-slate-500">{new Date(p.created_at).toLocaleDateString("fr-FR")}</div>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400">{p.status}</span>
              </div>
            ))}
          </div>
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="tenant-recent-opps">
            <h3 className="text-sm font-bold text-white mb-3">{t("dashboard.widget_recent_opportunities")}</h3>
            {(tenantDash.recent_opportunities || []).length === 0 ? (
              <p className="text-sm text-slate-500 py-4">{t("dashboard.no_opportunity_yet")}</p>
            ) : (tenantDash.recent_opportunities || []).map(o => (
              <div key={o.opportunity_id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                <div className="w-8 h-8 rounded-full bg-[#f7c935]/10 flex items-center justify-center text-xs font-bold text-[#f7c935]">{(o.title || "?")[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-white truncate">{o.title}</div>
                  <div className="text-[10px] text-slate-500">{o.amount?.toLocaleString("fr-FR")} EUR</div>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#f7c935]/10 text-[#f7c935]">{o.stage}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Team Info */}
        <div className="mt-4 flex items-center gap-4 bg-[#0b0811] border border-white/5 rounded-lg px-4 py-3">
          <Users className="w-5 h-5 text-[#f7c935]" />
          <span className="text-sm text-slate-300">{t("dashboard.team_label")} <strong className="text-white">{tenantDash.members}</strong> / {tenantDash.max_members} {t("dashboard.members_label")}</span>
          {user?.role === "tenant_admin" && (
            <button onClick={() => navigate("/tenant/team")} className="text-xs text-[#f7c935] hover:underline ml-auto">{t("dashboard.manage_team")}</button>
          )}
        </div>
      </div>
    );
  }

  // ─── ADMIN DASHBOARD ───
  if (isAdmin && dashboard) {
    const { stats, recent_clients = [], upcoming_meetings = [], pending_invitations = [], pending_staff_invitations = [], active_accompaniments = [], recent_fec = [], financial_summary = {} } = dashboard;

    const cancelClientInvitation = async (invId) => {
      try { await api.delete(`/admin/invitations/${invId}`); const d = await api.get("/admin/dashboard"); setDashboard(d.data); } catch {}
    };
    const cancelStaffInvitation = async (invId) => {
      try { await api.delete(`/admin/staff-invitations/${invId}`); const d = await api.get("/admin/dashboard"); setDashboard(d.data); } catch {}
    };

    const allInvitations = [
      ...pending_invitations.map(i => ({ ...i, inv_type: "client" })),
      ...pending_staff_invitations.map(i => ({ ...i, inv_type: "staff" })),
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    // KPI data — prestations (acc + services)
    const totalPrestationsActives = financial_summary.active_prestations || 0;

    return (
      <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="admin-dashboard">
        <OnboardingTour
          storageKey="blitzz_admin_dashboard_tour_v1"
          steps={[
            { selector: "admin-dashboard", title: "Tableau de bord administrateur", description: "Pilotez l'ensemble de l'activité Blitzz depuis ici : clients, accompagnements, finances, formations.", position: "bottom" },
            { selector: "admin-stats", title: "Vos 6 KPIs clés", description: "Clients, accompagnements, services, formations, factures, RDV — cliquez sur une carte pour accéder au module correspondant.", position: "bottom" },
            { selector: "hot-leads-widget", title: "Top leads à rappeler 🔥", description: "L'IA Gemini score vos prospects et opportunités d'après leurs appels Aircall et activités. Cliquez sur le téléphone vert pour click-to-call instantané.", position: "top" },
          ]}
        />
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
          <div>
            <h1 className="text-2xl sm:text-4xl font-black tracking-tighter">{t("dashboard.hello")}, <span style={{ color: "#f7c935" }}>{user?.first_name || user?.name?.split(" ")[0] || "Admin"}</span></h1>
            <p className="text-slate-500 mt-1 text-sm">{t("dashboard.subtitle_blitzz")}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setShowCreateClient(true)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold text-xs sm:text-sm" data-testid="create-client-btn">
              <Plus className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">{t("dashboard.btn_create")}</span> {t("dashboard.kpi_clients")}
            </Button>
            <Button onClick={() => setShowInvite(true)} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs sm:text-sm" data-testid="invite-btn">
              <UserPlus className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">{t("dashboard.btn_invite")}</span>
            </Button>
            <Button onClick={() => setShowSettings(true)} variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="widget-settings-btn">
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Primary KPIs */}
        {isVisible("stats") && (
          <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-6" data-testid="admin-stats">
            {[
              { icon: Users, label: t("dashboard.kpi_clients"), value: stats.total_clients, color: "#f7c935", to: "/admin/clients" },
              { icon: Route, label: t("dashboard.kpi_accompaniments"), value: stats.total_accompaniments, color: "#5a2d9c", to: "/admin/accompagnements" },
              { icon: Briefcase, label: t("dashboard.kpi_services"), value: stats.total_services, color: "#3b82f6", to: "/admin/services" },
              { icon: GraduationCap, label: t("dashboard.kpi_formations"), value: stats.total_formations, color: "#10b981", to: "/admin/formations" },
              { icon: Receipt, label: t("dashboard.kpi_invoices"), value: stats.total_invoices || 0, color: "#f97316", to: "/admin/facturation" },
              { icon: CalendarDays, label: t("dashboard.kpi_meetings"), value: stats.total_meetings, color: "#8b5cf6", to: "/admin/agenda" },
            ].map(({ icon: Icon, label, value, color, to }, i) => (
              <button key={i} onClick={() => navigate(to)} className="bg-[#14101c] border border-white/5 rounded-xl p-3 sm:p-4 text-left hover:border-white/15 transition-all animate-fade-in-up" style={{ animationDelay: `${i * 0.04}s` }} data-testid={`stat-${label.toLowerCase()}`}>
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 mb-1.5" style={{ color }} />
                <div className="text-xl sm:text-2xl font-black" style={{ color }}>{value || 0}</div>
                <div className="text-[10px] sm:text-xs text-slate-500 truncate">{label}</div>
              </button>
            ))}
          </div>
        )}

        {/* Hot Leads à rappeler — IA ranking (admin) */}
        <div className="mb-6 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <HotLeadsWidget limit={5} minScore={50} />
          </div>
          <div>
            <MetaInboxWidget />
          </div>
        </div>

        {/* Iter 322 — Activité partenaires (société active) */}
        <div className="mb-6">
          <PartnerDashboard societeId={currentSociete?.societe_id || null} compact />
        </div>

        {/* Financial + Graphs row */}
        {isVisible("financial_summary") && (
          <div className="grid lg:grid-cols-3 gap-4 mb-6">
            {/* Financial KPIs */}
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: "0.1s" }} data-testid="financial-widget">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#f7c935]" /> {t("dashboard.widget_finances")}</h3>
                <button onClick={() => navigate("/admin/financial")} className="text-[10px] font-medium hover:underline text-[#f7c935]">{t("dashboard.detail")}</button>
              </div>
              <div className="space-y-3">
                <div className="bg-[#0b0811] rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 mb-0.5">{t("dashboard.annual_ca")}</div>
                  <div className="text-lg font-black text-[#f7c935]">{(financial_summary.total_annual_ca || 0).toLocaleString("fr-FR")} EUR</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-[#0b0811] rounded-lg p-3">
                    <div className="text-[10px] text-slate-500 mb-0.5">{t("dashboard.monthly_revenue")}</div>
                    <div className="text-base font-black text-emerald-400">{(financial_summary.active_monthly_revenue || 0).toLocaleString("fr-FR")} EUR</div>
                  </div>
                  <div className="bg-[#0b0811] rounded-lg p-3">
                    <div className="text-[10px] text-slate-500 mb-0.5">{t("dashboard.active_prestations")}</div>
                    <div className="text-base font-black text-blue-400">{totalPrestationsActives}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* CA Mensuel Evolution */}
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: "0.14s" }} data-testid="ca-evolution-widget">
              <h3 className="text-sm font-bold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" /> {t("dashboard.widget_ca_monthly")}</h3>
              {caOverview?.evolution_mensuelle?.length > 0 ? (
                <ResponsiveContainer width="100%" height={140}>
                  <BarChart data={caOverview.evolution_mensuelle} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="mois" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <Tooltip contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 12 }}
                      formatter={(v, name) => [`${v.toLocaleString("fr-FR")} EUR`, name === "accompagnements" ? t("dashboard.kpi_accompaniments") : t("dashboard.kpi_services")]} />
                    <Bar dataKey="accompagnements" stackId="a" fill="#8b5cf6" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="services" stackId="a" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-500 py-4">{t("dashboard.no_data")}</p>}
              <div className="flex items-center gap-4 mt-2 justify-center">
                <span className="flex items-center gap-1.5 text-[10px] text-slate-400"><span className="w-2.5 h-2.5 rounded-sm bg-purple-500" />{t("dashboard.kpi_accompaniments")}</span>
                <span className="flex items-center gap-1.5 text-[10px] text-slate-400"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500" />{t("dashboard.kpi_services")}</span>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: "0.18s" }}>
              <h3 className="text-sm font-bold mb-3">{t("dashboard.widget_quick_actions")}</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: t("dashboard.qa_new_quote"), icon: FileText, to: "/admin/facturation", color: "#f7c935" },
                  { label: t("dashboard.qa_new_accomp"), icon: Route, to: "/admin/accompagnements", color: "#5a2d9c" },
                  { label: t("dashboard.qa_crm"), icon: Target, to: "/admin/crm", color: "#3b82f6" },
                  { label: t("dashboard.qa_accounting"), icon: BarChart3, to: "/admin/comptabilite", color: "#10b981" },
                ].map((a, i) => (
                  <button key={i} onClick={() => navigate(a.to)} className="p-3 rounded-lg bg-[#0b0811] border border-white/5 hover:border-white/15 transition-all text-left">
                    <a.icon className="w-4 h-4 mb-1.5" style={{ color: a.color }} />
                    <div className="text-[10px] sm:text-xs text-slate-300">{a.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CA par Client */}
        {caOverview?.ca_par_client?.length > 0 && (
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 mb-6 animate-fade-in-up" style={{ animationDelay: "0.19s" }} data-testid="ca-par-client-widget">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#f7c935]" /> {t("dashboard.widget_ca_per_client")}</h3>
              <span className="text-[10px] text-slate-500">{t("dashboard.top_clients", { count: caOverview.ca_par_client.length })}</span>
            </div>
            <ResponsiveContainer width="100%" height={Math.max(180, caOverview.ca_par_client.length * 32)}>
              <BarChart data={caOverview.ca_par_client} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : `${v}`} />
                <YAxis type="category" dataKey="client" tick={{ fill: "#94a3b8", fontSize: 11 }} width={120} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 12 }}
                  formatter={(v) => [`${v.toLocaleString("fr-FR")} EUR`, t("dashboard.ca_label")]} />
                <Bar dataKey="ca" radius={[0, 6, 6, 0]} fill="#f7c935" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Health Check synthétique (admin only) */}
        <div className="mb-6 animate-fade-in-up" style={{ animationDelay: "0.18s" }}>
          <HealthCheckMiniCard />
        </div>

        {/* Tresorerie */}
        {tresorerie && (
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 mb-6 animate-fade-in-up" style={{ animationDelay: "0.22s" }} data-testid="tresorerie-widget">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold flex items-center gap-2"><Wallet className="w-4 h-4 text-emerald-400" /> {t("dashboard.widget_treasury")}</h3>
              <span className="text-[10px] text-slate-500">{t("dashboard.tres_invoices_count", { count: tresorerie.nb_factures })}</span>
            </div>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-[#0b0811] rounded-lg p-3"><div className="text-[10px] text-slate-500">{t("dashboard.tres_emis")}</div><div className="text-lg font-black text-white">{tresorerie.total_emis?.toLocaleString("fr")} <span className="text-[10px] text-slate-500">EUR</span></div></div>
              <div className="bg-[#0b0811] rounded-lg p-3"><div className="text-[10px] text-slate-500">{t("dashboard.tres_encaisse")}</div><div className="text-lg font-black text-green-400">{tresorerie.total_encaisse?.toLocaleString("fr")} <span className="text-[10px] text-slate-500">EUR</span></div></div>
              <div className="bg-[#0b0811] rounded-lg p-3"><div className="text-[10px] text-slate-500">{t("dashboard.tres_impaye")}</div><div className="text-lg font-black text-red-400">{tresorerie.total_impaye?.toLocaleString("fr")} <span className="text-[10px] text-slate-500">EUR</span></div></div>
              <div className="bg-[#0b0811] rounded-lg p-3"><div className="text-[10px] text-slate-500">{t("dashboard.tres_devis_pending")}</div><div className="text-lg font-black text-blue-400">{tresorerie.total_devis_attente?.toLocaleString("fr")} <span className="text-[10px] text-slate-500">EUR</span></div></div>
            </div>
            {/* Cash flow chart */}
            {tresorerie.evolution?.length > 0 && (
              <div className="mb-4">
                <div className="text-[10px] text-slate-500 uppercase font-bold mb-2">{t("dashboard.tres_monthly_flow")}</div>
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={tresorerie.evolution} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="mois" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : `${v}`} />
                    <Tooltip contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 12 }}
                      formatter={(v, name) => [`${v?.toLocaleString("fr-FR")} EUR`, name === "emis" ? t("dashboard.tres_emis") : t("dashboard.tres_encaisse")]} />
                    <Bar dataKey="emis" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="encaisse" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-4 mt-1 justify-center">
                  <span className="flex items-center gap-1.5 text-[10px] text-slate-400"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500" />{t("dashboard.tres_emis")}</span>
                  <span className="flex items-center gap-1.5 text-[10px] text-slate-400"><span className="w-2.5 h-2.5 rounded-sm bg-green-500" />{t("dashboard.tres_encaisse")}</span>
                </div>
              </div>
            )}
            {/* Forecast 30/60/90 */}
            <div className="text-[10px] text-slate-500 uppercase font-bold mb-2">{t("dashboard.tres_forecast")}</div>
            <div className="grid grid-cols-3 gap-3">
              {tresorerie.forecast?.map(f => (
                <div key={f.period} className="bg-[#0b0811] rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 mb-1">{f.period}</div>
                  <div className="text-sm font-bold text-[#f7c935]">{f.total?.toLocaleString("fr")} EUR</div>
                  <div className="text-[9px] text-slate-600 mt-0.5">{t("dashboard.tres_payments")}: {f.expected_payments?.toLocaleString("fr")} | {t("dashboard.tres_quotes")}: {f.devis_potential?.toLocaleString("fr")}</div>
                </div>
              ))}
            </div>
            {tresorerie.overdue_count > 0 && (
              <div className="mt-3 flex items-center gap-2 p-2 rounded-lg bg-red-500/5 border border-red-500/10">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="text-xs text-red-400">{t(tresorerie.overdue_count > 1 ? "dashboard.tres_overdue_other" : "dashboard.tres_overdue_one", { count: tresorerie.overdue_count, amount: tresorerie.overdue_amount?.toLocaleString("fr") })}</span>
              </div>
            )}
          </div>
        )}

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Active Prestations */}
          {isVisible("active_accompaniments") && (
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold">{t("dashboard.widget_recent_prestations")}</h3>
                <div className="flex gap-1">
                  {[{ key: "active", label: t("dashboard.in_progress") }, { key: "termine", label: t("dashboard.completed_label") }].map(f => (
                    <button key={f.key} onClick={() => setFilter(f.key)}
                      className={`text-[10px] px-2.5 py-1 rounded-full font-medium transition-colors ${filter === f.key ? "bg-[#f7c935] text-[#14101c]" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}
                      data-testid={`filter-${f.key}`}>{f.label}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5 max-h-[240px] overflow-y-auto">
                {active_accompaniments.filter(a => filter === "active" ? a.status !== "termine" : a.status === "termine").length === 0 ? (
                  <p className="text-slate-500 text-sm py-4">{filter === "active" ? t("dashboard.no_active_accomp") : t("dashboard.no_finished_accomp")}</p>
                ) : active_accompaniments.filter(a => filter === "active" ? a.status !== "termine" : a.status === "termine").map(acc => (
                  <button key={acc.accompaniment_id} onClick={() => navigate(`/admin/accompagnements/${acc.accompaniment_id}`)}
                    className="w-full flex items-center justify-between p-2.5 rounded-lg bg-[#0b0811] border border-white/5 hover:border-white/15 text-left transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-xs truncate">{acc.title}</div>
                      <div className="text-[10px] text-slate-500 truncate">{acc.formula}{acc.price ? ` - ${acc.price}` : ""}</div>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-600 flex-shrink-0 ml-2" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Meetings */}
          {isVisible("upcoming_meetings") && (
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: "0.24s" }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold">{t("dashboard.widget_upcoming_meetings")}</h3>
                <button onClick={() => navigate("/admin/agenda")} className="text-[10px] font-medium hover:underline text-[#f7c935]">{t("dashboard.view_all")}</button>
              </div>
              <div className="space-y-1.5 max-h-[240px] overflow-y-auto">
                {upcoming_meetings.length === 0 ? (
                  <p className="text-slate-500 text-sm py-4">{t("dashboard.no_meeting")}</p>
                ) : upcoming_meetings.map(m => (
                  <div key={m.meeting_id} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-[#0b0811] border border-white/5">
                    <CalendarDays className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">{m.title}</div>
                      <div className="text-[10px] text-slate-500">{new Date(m.date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })} {m.time_start}</div>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${m.meeting_type === "visio" ? "bg-blue-500/10 text-blue-400" : "bg-green-500/10 text-green-400"}`}>
                      {m.meeting_type === "visio" ? t("dashboard.client_visio") : t("dashboard.client_presentiel")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Clients */}
          {isVisible("recent_clients") && (
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: "0.28s" }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold">{t("dashboard.widget_recent_clients")}</h3>
                <button onClick={() => navigate("/admin/clients")} className="text-[10px] font-medium hover:underline text-[#f7c935]">{t("dashboard.view_all")}</button>
              </div>
              <div className="space-y-1.5 max-h-[240px] overflow-y-auto">
                {recent_clients.map(c => (
                  <button key={c.user_id} onClick={() => navigate(`/admin/clients/${c.user_id}`)}
                    className="w-full flex items-center gap-2.5 p-2.5 rounded-lg bg-[#0b0811] border border-white/5 hover:border-white/15 text-left transition-colors">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0" style={{ background: "#461d8a", color: "#f7c935" }}>
                      {(c.name || c.email)?.[0]?.toUpperCase() || "C"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">{c.name || t("dashboard.client_no_name")}</div>
                      <div className="text-[10px] text-slate-500 truncate">{c.email}</div>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Invitations */}
          {isVisible("pending_invitations") && (
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: "0.32s" }} data-testid="invitations-widget">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold">{t("dashboard.widget_invitations")}</h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 font-medium">{allInvitations.length}</span>
              </div>
              <div className="space-y-1.5 max-h-[240px] overflow-y-auto">
                {allInvitations.length === 0 ? (
                  <p className="text-slate-500 text-sm py-4">{t("dashboard.no_invitation")}</p>
                ) : allInvitations.map(inv => (
                  <div key={inv.invitation_id} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-[#0b0811] border border-white/5" data-testid={`invitation-${inv.invitation_id}`}>
                    {inv.inv_type === "staff" ? <Shield className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" /> : <UserPlus className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">{inv.name || inv.email}</div>
                      <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                        <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${inv.inv_type === "staff" ? "bg-blue-500/10 text-blue-400" : "bg-purple-500/10 text-purple-400"}`}>
                          {inv.inv_type === "staff" ? t("dashboard.type_staff") : t("dashboard.type_client")}
                        </span>
                        <span>{new Date(inv.created_at).toLocaleDateString("fr-FR")}</span>
                      </div>
                    </div>
                    <button onClick={() => inv.inv_type === "staff" ? cancelStaffInvitation(inv.invitation_id) : cancelClientInvitation(inv.invitation_id)}
                      className="p-1 rounded hover:bg-red-500/10 flex-shrink-0" data-testid={`cancel-inv-${inv.invitation_id}`}>
                      <X className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Routines IA KPIs */}
          {routineStats && (
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-fade-in-up lg:col-span-2" style={{ animationDelay: "0.36s" }} data-testid="routine-kpis-widget">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#f7c935]" /> {t("dashboard.widget_ai_routines")}</h3>
                <button onClick={() => navigate("/admin/crm")} className="text-[10px] font-medium hover:underline text-[#f7c935]">{t("dashboard.open_crm")}</button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-[#0b0811] rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 mb-0.5">{t("dashboard.rt_total_executions")}</div>
                  <div className="text-xl font-black text-[#f7c935]">{routineStats.total}</div>
                </div>
                <div className="bg-[#0b0811] rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 mb-0.5">{t("dashboard.rt_this_week")}</div>
                  <div className="text-xl font-black text-emerald-400">{routineStats.this_week}</div>
                </div>
                <div className="bg-[#0b0811] rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 mb-0.5">{t("dashboard.rt_this_month")}</div>
                  <div className="text-xl font-black text-blue-400">{routineStats.this_month}</div>
                </div>
                <div className="bg-[#0b0811] rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 mb-0.5">{t("dashboard.rt_weekly_trend")}</div>
                  <div className="flex items-end gap-1 h-8">
                    {(routineStats.weekly_trend || []).map((w, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                        <div className="w-full rounded-sm" style={{ height: Math.max(4, (w.count / Math.max(1, ...routineStats.weekly_trend.map(t => t.count))) * 28), background: i === routineStats.weekly_trend.length - 1 ? "#f7c935" : "#f7c935" + "40" }} />
                        <span className="text-[8px] text-slate-600">{w.week}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* By type detail */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { type: "prequalification", label: t("dashboard.rt_prequalification"), icon: UserSearch, color: "#3b82f6" },
                  { type: "daily_calls", label: t("dashboard.rt_daily_calls"), icon: PhoneCall, color: "#22c55e" },
                  { type: "devis_relance", label: t("dashboard.rt_devis_relance"), icon: FileText, color: "#f59e0b" },
                ].map(r => {
                  const stats = routineStats.by_type?.[r.type] || {};
                  const last = routineStats.last_executions?.[r.type];
                  return (
                    <div key={r.type} className="bg-[#0b0811] rounded-lg p-3 border border-white/5" data-testid={`routine-kpi-${r.type}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: r.color + "20" }}>
                          <r.icon className="w-3.5 h-3.5" style={{ color: r.color }} />
                        </div>
                        <div className="text-xs font-bold text-white">{r.label}</div>
                      </div>
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="text-lg font-black" style={{ color: r.color }}>{stats.total || 0}</span>
                        <span className="text-[10px] text-slate-500">{t("dashboard.rt_total_short")}</span>
                        {stats.this_week > 0 && (
                          <span className="text-[10px] text-emerald-400 flex items-center gap-0.5">
                            <ArrowUpRight className="w-3 h-3" /> {stats.this_week} {t("dashboard.rt_this_week_short")}
                          </span>
                        )}
                      </div>
                      {last ? (
                        <div className="text-[10px] text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(last.executed_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                          {last.executed_by && <span>{t("dashboard.rt_by")} {last.executed_by}</span>}
                        </div>
                      ) : (
                        <div className="text-[10px] text-slate-600">{t("dashboard.rt_never_executed")}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Dialogs */}
        <Dialog open={showCreateClient} onOpenChange={setShowCreateClient}>
          <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
            <DialogHeader><DialogTitle className="text-lg font-bold">{t("dashboard.dlg_create_client_title")}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-3">
              <Input placeholder={t("dashboard.dlg_full_name")} value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid="create-client-name" />
              <Input placeholder={t("dashboard.dlg_email")} type="email" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid="create-client-email" />
              <Input placeholder={t("dashboard.dlg_password")} type="password" value={clientForm.password} onChange={(e) => setClientForm({ ...clientForm, password: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid="create-client-password" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder={t("dashboard.dlg_company")} value={clientForm.company_name} onChange={(e) => setClientForm({ ...clientForm, company_name: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
                <Input placeholder={t("dashboard.dlg_phone")} value={clientForm.phone} onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
              </div>
              <Button onClick={handleCreateClient} disabled={creating || !clientForm.name || !clientForm.email || !clientForm.password} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="submit-create-client">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} {t("dashboard.dlg_create_client_btn")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showInvite} onOpenChange={(v) => { setShowInvite(v); if (!v) { setInvLink(""); setInvForm({ email: "", name: "", company_name: "" }); } }}>
          <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
            <DialogHeader><DialogTitle className="text-lg font-bold">{t("dashboard.dlg_invite_title")}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-3">
              {!invLink ? (<>
                <Input placeholder={t("dashboard.dlg_email")} value={invForm.email} onChange={(e) => setInvForm({ ...invForm, email: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid="invite-email" />
                <Input placeholder={t("dashboard.dlg_name")} value={invForm.name} onChange={(e) => setInvForm({ ...invForm, name: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
                <Input placeholder={t("dashboard.dlg_company")} value={invForm.company_name} onChange={(e) => setInvForm({ ...invForm, company_name: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
                <Button onClick={handleInvite} disabled={!invForm.email} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="submit-invite">
                  <UserPlus className="w-4 h-4 mr-2" /> {t("dashboard.dlg_generate_link")}
                </Button>
              </>) : (
                <div>
                  <p className="text-sm text-green-400 mb-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {t("dashboard.dlg_invitation_created")}</p>
                  <div className="flex items-center gap-2">
                    <Input value={invLink} readOnly className="bg-[#0b0811] border-white/10 text-white text-xs" />
                    <Button onClick={copyLink} size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5 flex-shrink-0">
                      {copied ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-md">
            <DialogHeader><DialogTitle className="text-lg font-bold">{t("dashboard.dlg_widgets_title")}</DialogTitle></DialogHeader>
            <div className="space-y-2 mt-3">
              {widgets.map(w => (
                <button key={w.id} onClick={() => toggleWidget(w.id)} className="w-full flex items-center justify-between p-3 rounded-lg bg-[#0b0811] border border-white/5 hover:border-white/10 transition-colors">
                  <span className="text-sm font-medium">{w.title}</span>
                  {w.visible !== false ? <Eye className="w-4 h-4 text-green-400" /> : <EyeOff className="w-4 h-4 text-slate-600" />}
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─── CLIENT DASHBOARD ───
  const accs = dashboard?.accompaniments || [];
  const srvs = dashboard?.services || [];
  const mtgs = dashboard?.meetings || [];
  const activeAccs = accs.filter(a => a.status !== "termine");
  const doneAccs = accs.filter(a => a.status === "termine");
  const upcomingMtgs = mtgs.filter(m => m.date >= new Date().toISOString().slice(0, 10)).slice(0, 5);
  const totalTasks = accs.reduce((s, a) => s + (a.phases?.reduce((ss, p) => ss + (p.tasks?.length || 0), 0) || 0), 0);
  const doneTasks = accs.reduce((s, a) => s + (a.phases?.reduce((ss, p) => ss + (p.tasks?.filter(t => t.status === "done").length || 0), 0) || 0), 0);
  const overallPct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="client-dashboard">
      {/* Onboarding wizard for new tenants */}
      {showOnboarding && <OnboardingWizardPage onComplete={() => setShowOnboarding(false)} />}
      {/* Admin impersonation banner */}
      {localStorage.getItem("blitzz_admin_token") && (
        <div className="bg-[#f7c935]/10 border border-[#f7c935]/30 rounded-xl p-3 mb-4 flex items-center justify-between" data-testid="impersonation-banner">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#f7c935]" />
            <span className="text-sm text-[#f7c935] font-medium">{t("dashboard.client_impersonation", { name: user?.name || t("dashboard.client_default_name") })}</span>
          </div>
          <button onClick={() => {
            const adminToken = localStorage.getItem("blitzz_admin_token");
            localStorage.removeItem("blitzz_admin_token");
            localStorage.setItem("blitzz_token", adminToken);
            window.location.href = "/admin/clients";
          }} className="bg-[#f7c935] text-[#0b0811] px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-[#e6b825] transition-colors" data-testid="return-admin-btn">
            {t("dashboard.client_return_admin")}
          </button>
        </div>
      )}
      {/* Hero */}
      <div className="relative bg-gradient-to-br from-[#0b0811] to-[#14101c] border border-white/10 rounded-2xl p-6 sm:p-8 mb-6 overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#f7c935]/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
        <div className="relative">
          {dashboard?.clientHome?.logo && (
            <img src={dashboard.clientHome.logo} alt="" className="w-12 h-12 rounded-xl object-cover mb-3 border border-white/10" />
          )}
          <h1 className="text-2xl sm:text-4xl font-black tracking-tighter">
            {t("dashboard.client_welcome")}, <span style={{ color: "#f7c935" }}>{user?.first_name || user?.name?.split(" ")[0] || "Client"}</span>
            {dashboard?.clientHome?.tenant_name && <span className="text-slate-500 text-lg font-medium ml-2">- {dashboard.clientHome.tenant_name}</span>}
          </h1>
          <p className="text-slate-400 mt-1 text-sm">{dashboard?.clientHome?.welcome_text || t("dashboard.client_your_space")}</p>
          {totalTasks > 0 && (
            <div className="mt-4 max-w-sm">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-slate-400">{t("dashboard.client_overall_progress")}</span>
                <span className="text-sm font-black" style={{ color: overallPct >= 80 ? "#22c55e" : "#f7c935" }}>{overallPct}%</span>
              </div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${overallPct}%`, background: `linear-gradient(90deg, #f7c935, ${overallPct >= 80 ? "#22c55e" : "#f7c935"})` }} />
              </div>
              <p className="text-[10px] text-slate-600 mt-1">{t("dashboard.client_n_tasks", { done: doneTasks, total: totalTasks })}</p>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { icon: Route, label: t("dashboard.kpi_accompaniments"), value: accs.length, color: "#f7c935", sub: `${activeAccs.length} ${t("dashboard.client_active_short")}` },
          { icon: Briefcase, label: t("dashboard.kpi_services"), value: srvs.length, color: "#3b82f6", sub: t("dashboard.client_actives") },
          { icon: CalendarDays, label: t("dashboard.client_meetings_upcoming"), value: upcomingMtgs.length, color: "#8b5cf6", sub: upcomingMtgs[0]?.date || "-" },
          { icon: Clock, label: t("dashboard.client_tasks_label"), value: `${doneTasks}/${totalTasks}`, color: "#10b981", sub: t("dashboard.client_tasks_completed") },
        ].map(({ icon: Icon, label, value, color, sub }, i) => (
          <div key={i} className="bg-[#14101c] border border-white/5 rounded-xl p-3 sm:p-4 hover:border-white/15 transition-colors animate-fade-in-up" style={{ animationDelay: `${i * 0.04}s` }}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5 mb-1.5" style={{ color }} />
            <div className="text-xl sm:text-2xl font-black" style={{ color }}>{value}</div>
            <div className="text-[10px] sm:text-xs text-slate-500">{label}</div>
            <div className="text-[10px] text-slate-600 mt-0.5">{sub}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* En cours */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" /> {t("dashboard.in_progress")}
          </h3>
          {activeAccs.length === 0 ? <p className="text-slate-500 text-sm py-4">{t("dashboard.no_active_accomp")}</p> : activeAccs.map(acc => {
            const total = acc.phases?.reduce((s, p) => s + (p.tasks?.length || 0), 0) || 0;
            const done = acc.phases?.reduce((s, p) => s + (p.tasks?.filter(t => t.status === "done").length || 0), 0) || 0;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <div key={acc.accompaniment_id} className="p-3 rounded-xl bg-[#0b0811] border border-white/5 mb-2 hover:border-white/10 transition-colors">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="font-medium text-xs">{acc.title}</div>
                  <span className="text-xs font-bold" style={{ color: pct >= 80 ? "#22c55e" : "#f7c935" }}>{pct}%</span>
                </div>
                <div className="text-[10px] text-slate-500 mb-2">{acc.formula} - {total > 0 ? t("dashboard.client_n_tasks", { done, total }) : t("dashboard.client_no_phases")}</div>
                {total > 0 && (
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: pct >= 80 ? "#22c55e" : "#f7c935" }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Prochains RDV */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
          <h3 className="text-sm font-bold mb-3">{t("dashboard.widget_upcoming_meetings")}</h3>
          {upcomingMtgs.length === 0 ? <p className="text-slate-500 text-sm py-4">{t("dashboard.no_meeting")}</p> : upcomingMtgs.map(m => (
            <div key={m.meeting_id} className="flex items-center gap-2.5 p-3 rounded-xl bg-[#0b0811] border border-white/5 mb-2 hover:border-white/10 transition-colors">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: m.meeting_type === "visio" ? "#3b82f620" : "#10b98120" }}>
                <CalendarDays className="w-4 h-4" style={{ color: m.meeting_type === "visio" ? "#3b82f6" : "#10b981" }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{m.title}</div>
                <div className="text-[10px] text-slate-500">{new Date(m.date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })} a {m.time_start}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Services actifs */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: "0.25s" }}>
          <h3 className="text-sm font-bold mb-3">{t("dashboard.widget_my_services")}</h3>
          {srvs.length === 0 ? <p className="text-slate-500 text-sm py-4">{t("dashboard.no_active_service")}</p> : srvs.map(srv => (
            <div key={srv.service_id} className="flex items-center justify-between p-3 rounded-xl bg-[#0b0811] border border-white/5 mb-2 hover:border-white/10 transition-colors">
              <div className="flex items-center gap-2.5">
                <Briefcase className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                <div className="min-w-0"><div className="text-xs font-medium truncate">{srv.name}</div><div className="text-[10px] text-slate-500">{srv.category}</div></div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${srv.status === "active" ? "bg-green-500/10 text-green-400" : "bg-slate-500/10 text-slate-400"}`}>{srv.status === "active" ? t("dashboard.active_label") : srv.status}</span>
            </div>
          ))}
        </div>

        {/* Termines */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-400" /> {t("dashboard.completed_label")}
          </h3>
          {doneAccs.length === 0 ? <p className="text-slate-500 text-sm py-4">{t("dashboard.no_finished_accomp")}</p> : doneAccs.map(acc => (
            <div key={acc.accompaniment_id} className="p-3 rounded-xl bg-[#0b0811] border border-white/5 mb-2">
              <div className="flex items-center justify-between">
                <div className="font-medium text-xs">{acc.title}</div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/10 text-green-400">{t("dashboard.completed_singular")}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
